import { CircularProgress, Paper, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { SessionActivity } from "./interface";

interface BriefTableProps {
  sessionActivities: SessionActivity[];
  pdfIdNamePairs: { [key: string]: string };
  loading: boolean;
  setPageTrans: (value: number) => void;
}

interface SessionActivityRow {
  userId: string;
  sessionId: string;
  sessionStartTime: Date;
  totalWatchTime: number;
  chatActionCount: number;
  pdfActionCount: number;
  otherActionCount: number;
}

interface ActionRow {
  userId: string;
  sessionId: string;
  actionTimestamp: Date;
  actionName: string;
  prompt: string;
  response: string;
  pdfName: string;
  pdfType: string;
  page: number;
  engineChoice: string;
}

export const BriefTable: React.FC<BriefTableProps> = ({
  sessionActivities,
  pdfIdNamePairs,
  loading,
  setPageTrans,
}) => {
  const [sessionData, setSessionData] = useState<SessionActivityRow[]>([]);
  const [actionData, setActionData] = useState<ActionRow[]>([]);

  useEffect(() => {
    // map session activites
    const sessionData = sessionActivities.map((sessionActivity) => {
      return {
        id: uuidv4(),
        userId: sessionActivity.userId,
        sessionId: sessionActivity.sessionId,
        sessionStartTime: new Date(sessionActivity.sessionStartTime),
        totalWatchTime: sessionActivity.totalWatchTime,
        codeActionCount: sessionActivity.codeActions.length,
        chatActionCount: sessionActivity.chatActions.length,
        pdfActionCount: sessionActivity.pdfActions.length,
        otherActionCount: sessionActivity.otherActions.length,
      };
    });
    setSessionData(sessionData);

    // map action data
    // const codeActionData = sessionActivities.map((sessionActivity) => {
    //   return sessionActivity.codeActions.map((codeAction) => {
    //     return {
    //       id: uuidv4(),
    //       userId: sessionActivity.userId,
    //       sessionId: sessionActivity.sessionId,
    //       actionTimestamp: new Date(Number(codeAction.timestamp)),
    //       actionName: codeAction.name,
    //       actionDetails: JSON.stringify(codeAction, null, 2),
    //     };
    //   });
    // });
    const chatActionData = sessionActivities.map((sessionActivity) => {
      return sessionActivity.chatActions.map((chatAction) => {
        return {
          id: uuidv4(),
          userId: sessionActivity.userId,
          sessionId: sessionActivity.sessionId,
          actionTimestamp: new Date(Number(chatAction.timestamp)),
          actionName: chatAction.name,
          prompt: chatAction.context.content.prompt,
          response: chatAction.context.content.answer,
          pdfName: pdfIdNamePairs[chatAction.context.pdfID],
          pdfType: chatAction.context.pdfMaterialType,
          page: chatAction.context.pdfPage,
          engineChoice: chatAction.context.engineChoice || "openai",
        };
      });
    });
    // const pdfActionData = sessionActivities.map((sessionActivity) => {
    //   return sessionActivity.pdfActions.map((pdfAction) => {
    //     return {
    //       id: uuidv4(),
    //       userId: sessionActivity.userId,
    //       sessionId: sessionActivity.sessionId,
    //       actionTimestamp: new Date(Number(pdfAction.timestamp)),
    //       actionName: pdfAction.name,
    //       actionDetails: JSON.stringify(pdfAction, null, 2),
    //     };
    //   });
    // });
    const actionData = [...chatActionData].flat();
    setActionData(actionData);
  }, [sessionActivities, pdfIdNamePairs]);

  const sessionActivityColumns: GridColDef[] = [
    { field: "userId", headerName: "User ID", minWidth: 150, flex: 1 },
    { field: "sessionId", headerName: "Session ID", minWidth: 150, flex: 1 },
    {
      field: "sessionStartTime",
      headerName: "Session Start Time",
      width: 200,
      type: "dateTime",
    },
    {
      field: "totalWatchTime",
      headerName: "Total Watch Time (mins)",
      type: "number",
    },
    {
      field: "codeActionCount",
      headerName: "Code Action Count",
      type: "number",
    },
    {
      field: "chatActionCount",
      headerName: "Chat Action Count",
      type: "number",
    },
    {
      field: "pdfActionCount",
      headerName: "PDF Action Count",
      type: "number",
    },
    {
      field: "otherActionCount",
      headerName: "Other Action Count",
      type: "number",
    },
  ];

  const actionColumns: GridColDef[] = [
    { field: "userId", headerName: "User ID", width: 150 },
    { field: "sessionId", headerName: "Session ID", width: 150 },
    {
      field: "actionTimestamp",
      headerName: "Action Timestamp",
      width: 200,
      type: "dateTime",
    },
    { field: "prompt", headerName: "Student Question", width: 150 },
    { field: "response", headerName: "KIWI Answer", width: 400 },
    { field: "pdfName", headerName: "File Name", width: 150 },
    { field: "pdfType", headerName: "Type", width: 100 },
    { field: "page", headerName: "Page", width: 50 },
    { field: "engineChoice", headerName: "Engine Choice", width: 100 },
  ];

  return (
    <div style={{ width: "100%" }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Typography variant="h4">Per Session Details</Typography>
          <Paper style={{ height: "400px", width: "100%" }}>
            <DataGrid
              rows={sessionData}
              columns={sessionActivityColumns}
              pagination
            />
          </Paper>
          {/* Per Action Table Below */}
          <Typography variant="h4">Chat Details</Typography>
          <Paper style={{ height: "800px", width: "100%" }}>
            <DataGrid
              rows={actionData}
              columns={actionColumns}
              pagination
              getRowHeight={() => "auto"}
            />
          </Paper>
        </>
      )}
    </div>
  );
};
